import { Company } from "@domain/CRM/company";
import { Grower } from "@domain/CRM/grower";
import { Job, JobStatusNumber } from "@domain/CRM/job";
import { Filter, StandardAxiosConfig, getBaseDataURL, parseDates } from "../../../common";
import { CreateAPI_Base, DataAPI_Base, GetAPI_Base, UpdateAPI_Base } from "../base";
import { apiClient } from "../../../client";
import { IAirtableAttachment } from "@domain/CRM/Attachment";
/**
 * Jobs create API — transport-only. The `many`/`one` methods are inherited
 * from `CreateAPI_Base` and make the raw HTTP POST. Callers that need the
 * full creation pipeline (associate growers, salespersons, branches, prep
 * payload) should use `submitJobs` / `submitJob` from
 * `domain/job/jobCreationService` instead of calling this class directly.
 */
class CreateAPI_Jobs extends CreateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}
}

class GetAPI_Jobs extends GetAPI_Base<Job> {
	constructor() {
		super("jobs", Job, true);
	}

	public async byCompany(company: Company) {
		if (!company.name) return [];
		const filter: Filter<Job> = { fields: { companyName: company.name } };
		const jobs = await super.byFilter(filter);
		return jobs;
	}

	/**
	 * Retrieves jobs associated with the specified growers.
	 *
	 * @param growers - An array of `Grower` objects to filter jobs by. If empty or undefined, returns an empty array.
	 * @param isUnreadyOnly - Optional. If `true`, filters jobs to only those that are unready (`statusNumber` is `0`). Defaults to `false`.
	 */
	public async byGrowers(growers: Grower[], isUnreadyOnly: boolean = false): Promise<Job[]> {
		if (!growers || growers.length === 0) return [];
		const growerFilter: Filter<Job> = {
			type: "OR",
			fields: [
				// We have to search both, because Grower names are not normalized
				...growers.map((g) => ({ growerIds: [g.name] })),
				...growers.map((g) => ({ growerName: g.name })),
			],
		};
		const unreadyFilter: Filter<Job> = {
			type: "AND",
			fields: [{ statusNumber: JobStatusNumber.Unready }, growerFilter],
		};
		const jobs = await super.byFilter(isUnreadyOnly ? unreadyFilter : growerFilter);
		return jobs;
	}

	public async lastCropOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/last-crop`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get last crop options`);
		} else {
			return response.data;
		}
	}

	public async nextCropOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/next-crop`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get next crop options`);
		} else {
			return response.data;
		}
	}
}

class UpdateAPI_Jobs extends UpdateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}

	/**
	 * Update a single field on a single Job. `field` is a `keyof Job` (e.g.
	 * `"dealIds"`, `"branchIds"`); the BE translates this to the underlying
	 * Airtable column. `value` must be in the shape Airtable expects: `["recXXX"]`
	 * for a linked-record field, a string for text, etc.
	 *
	 * Use this instead of `many([fullJob])` when you've loaded a Job via a lite
	 * payload (matcher pages, etc.) — `many` round-trips every field, which would
	 * clear any column not present in the lite payload.
	 */
	async field<K extends keyof Job>(id: string, field: K, value: unknown): Promise<Job> {
		const url = `${getBaseDataURL("jobs")}/update/${id}/${field}`;
		const response = await apiClient.post<Job>(url, value, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to update '${String(field)}' on job ${id}: ${response.statusText}`);
		}
		const updated = Object.assign(new Job(), response.data);
		parseDates<Job>([updated]);
		return updated;
	}

	async files(id: string, fileList: File[]) {
		const url = `${getBaseDataURL("jobs")}/upload-file/${id}`;

		async function upload(file: File) {
			const formData = new FormData();
			formData.append("file", file);

			const response = await apiClient.post(url, formData, StandardAxiosConfig(10, { isFileUpload: true }));
			if (response.status !== 200) {
				throw new Error(`Failed to upload file: ${file.name}`);
			} else {
				return response.data;
			}
		}

		if (!fileList) return [];
		const fileArray = Array.from(fileList);
		const uploadResults = await Promise.all(
			fileArray.map(async (file) => {
				const s3url = await upload(file);
				return { url: s3url, filename: file.name } as IAirtableAttachment;
			}),
		);

		return uploadResults;
	}
}

class JobIssuesAPI extends DataAPI_Base<Job> {
	constructor() {
		super("jobs", Job);
	}

	async getAgrian(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/agrian`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get Agrian issues`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async fixAgrian(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/agrian`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Agrian issues`);
		} else {
			return response.data;
		}
	}

	async getDataSendback(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/data-sendback`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get data sendback issues`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async getDealMatcherJobs(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/deal-matcher`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unmatched deal jobs`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async getBranchMatcherJobs(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/branch-matcher`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unmatched branch jobs`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async fixDealMatcherJobs(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/deal-matcher`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix unmatched deal jobs`);
		} else {
			return response.data;
		}
	}

	async getExeMissionValidationJobs(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/exe-mission-validation`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get Exe Mission Validation jobs`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async fixExeMissionValidationJobs(ids: string[], force = false): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/exe-mission-validation${force ? "?force=true" : ""}`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Exe Mission Validation jobs`);
		} else {
			return response.data;
		}
	}

	async fixDataSendback(ids: string[], forceEmail = false, forceApi = false): Promise<void> {
		const params = new URLSearchParams();
		if (forceEmail) params.set("force_email", "true");
		if (forceApi) params.set("force_api", "true");
		const qs = params.toString();
		const url = `${getBaseDataURL("jobs")}/issues/data-sendback${qs ? `?${qs}` : ""}`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix data sendback issues`);
		}
		return response.data;
	}
}
export class JobsAPI extends DataAPI_Base<Job> {
	get: GetAPI_Jobs;
	create: CreateAPI_Jobs;
	update: UpdateAPI_Jobs;
	issues: JobIssuesAPI;

	// job data is compressed, as it can get quite large
	constructor() {
		super("jobs", Job, true);
		this.get = new GetAPI_Jobs();
		this.create = new CreateAPI_Jobs();
		this.update = new UpdateAPI_Jobs();
		this.issues = new JobIssuesAPI();
	}
}
